/* =========================================================================
   IBC 2.0 — WISHLIST
   A small localStorage wishlist. Nothing is written to the cart: the live
   theme kept the list in a `wishlist` cart attribute, which leaked into
   every order and is not reproduced here.

   Storage key: `ptg_wishlist_v1` — the key the live theme already uses, so
   a customer's existing list carries straight over.
   Shape: a JSON array of product handles, e.g. ["gianduia-bar","cocoa-tin"].

   Public API — window.IbcWishlist
     list()            -> array of handles (a copy)
     count()           -> number
     has(handle)       -> boolean
     add(handle)       -> boolean (true if it was added)
     remove(handle)    -> boolean (true if it was removed)
     toggle(handle)    -> boolean (true if it is now saved)
     clear()           -> void
     refresh()         -> repaint every badge and toggle on the page
     destroy()         -> unbind everything (see TEARDOWN at the foot)

   Event: `ibc:wishlist-changed` on document, bubbling, with
     detail = { list, count, handle, saved }

   -------------------------------------------------------------------------
   HYDRATION — why there is more than a DOMContentLoaded paint
   -------------------------------------------------------------------------
   Nothing about the saved state is server-rendered: every
   `[data-ibc-wishlist-toggle]` arrives from Liquid as `aria-pressed="false"`
   and is painted here. That is fine for a page load, and wrong for every
   other way a toggle can reach the DOM.

   Since the product tile carries a heart again
   (snippets/ibc-product-card.liquid), the important case is the collection
   filter: assets/ibc-filters.js replaces the whole grid with
   `inner.innerHTML = fresh.innerHTML`, so the painted cards are thrown away
   and a fresh set of unpainted ones takes their place. That fires no
   `shopify:section:load` — it is not a section render — so the old wiring
   would have left every heart on a filtered grid reading "not saved"
   whatever the customer had actually saved.

   Three nets, cheapest first:

     1. CLICKS never needed hydration. The one listener is delegated from
        `document`, so a heart works the instant it exists, painted or not.
     2. `ibc:filters:rendered` — dispatched by assets/ibc-filters.js after
        both of its swaps. This is the exact, cheap signal for the case
        above.
     3. A MutationObserver as the general net, for injection paths that
        announce nothing (predictive search writes `panel.innerHTML`
        directly, and the quick-view dialog builds its own markup). It only
        repaints when an added subtree actually contains a toggle or a
        count, so an unrelated DOM change costs one `querySelector` call.

   The observer has to not feed itself: `paint()` writes into the label
   span, which is a childList mutation on a node it is watching. It sets a
   flag for the duration and drains the queue with `takeRecords()` on the
   way out, and every write is guarded on the value actually changing.
   ========================================================================= */

(function () {
  'use strict';

  /* Re-entry guard. This file is loaded once, by layout/theme.liquid, but a
     controller that binds document-level listeners must never be able to
     bind them twice — a second copy would toggle every heart twice per
     click, which reads as the button not working at all. */
  if (window.IbcWishlist) return;

  var KEY = 'ptg_wishlist_v1';
  var EVENT = 'ibc:wishlist-changed';

  /* ---- storage ---------------------------------------------------------- */

  function readRaw() {
    try {
      return window.localStorage.getItem(KEY);
    } catch (error) {
      /* Private mode, or storage disabled. The wishlist simply stays empty. */
      return null;
    }
  }

  function normalise(value) {
    var out = [];
    if (!Array.isArray(value)) return out;
    for (var i = 0; i < value.length; i++) {
      var entry = value[i];
      var handle = null;
      if (typeof entry === 'string') {
        handle = entry;
      } else if (entry && typeof entry === 'object' && typeof entry.handle === 'string') {
        /* Tolerate an older object shape rather than throwing the list away. */
        handle = entry.handle;
      }
      if (!handle) continue;
      handle = handle.trim();
      if (handle && out.indexOf(handle) === -1) out.push(handle);
    }
    return out;
  }

  function read() {
    var raw = readRaw();
    if (!raw) return [];
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return [];
    }
    return normalise(parsed);
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (error) {
      /* Nothing useful to do: the in-page state is still correct. */
    }
  }

  /* ---- painting --------------------------------------------------------- */

  function plural(count) {
    return count === 1 ? '1 saved item' : count + ' saved items';
  }

  /* True for the duration of a paint, so the MutationObserver below can tell
     its own writes apart from somebody else's. */
  var painting = false;
  var observer = null;

  /* Every write is guarded on the value changing. That keeps the observer
     quiet, and it also means a repaint over an already-correct grid does no
     DOM work at all — which matters when the net fires on every injection. */
  function setText(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  function paint(list) {
    painting = true;

    try {
      var count = list.length;

      var badges = document.querySelectorAll('[data-ibc-wishlist-count]');
      for (var i = 0; i < badges.length; i++) {
        setText(badges[i], String(count));
        if (badges[i].hidden !== (count === 0)) badges[i].hidden = count === 0;
      }

      var labels = document.querySelectorAll('[data-ibc-wishlist-label]');
      for (var j = 0; j < labels.length; j++) {
        setText(labels[j], count === 0 ? 'Wishlist, empty' : 'Wishlist, ' + plural(count));
      }

      var toggles = document.querySelectorAll('[data-ibc-wishlist-toggle]');
      for (var k = 0; k < toggles.length; k++) {
        var button = toggles[k];
        var handle = button.getAttribute('data-ibc-wishlist-handle');
        if (!handle) continue;
        var saved = list.indexOf(handle) !== -1;
        var pressed = saved ? 'true' : 'false';
        if (button.getAttribute('aria-pressed') !== pressed) {
          button.setAttribute('aria-pressed', pressed);
        }

        var label = button.querySelector('[data-ibc-wishlist-btn-label]');
        if (label) {
          var title = button.getAttribute('data-ibc-wishlist-title') || 'this product';
          setText(
            label,
            saved
              ? 'Remove ' + title + ' from your wishlist'
              : 'Save ' + title + ' to your wishlist'
          );
        }
      }
    } finally {
      painting = false;
      /* Drop anything this paint just queued, so the observer does not wake
         up on its own handiwork. Synchronous, and the observer callback is a
         microtask, so this always runs first. */
      if (observer) observer.takeRecords();
    }
  }

  function announce(list, handle, saved) {
    paint(list);

    var detail = { list: list.slice(), count: list.length, handle: handle || null, saved: !!saved };
    var event;
    if (typeof window.CustomEvent === 'function') {
      event = new CustomEvent(EVENT, { bubbles: true, detail: detail });
    } else {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(EVENT, true, false, detail);
    }
    document.dispatchEvent(event);
  }

  /* ---- API -------------------------------------------------------------- */

  function clean(handle) {
    return typeof handle === 'string' ? handle.trim() : '';
  }

  var IbcWishlist = {
    key: KEY,

    list: function () {
      return read();
    },

    count: function () {
      return read().length;
    },

    has: function (handle) {
      var value = clean(handle);
      if (!value) return false;
      return read().indexOf(value) !== -1;
    },

    add: function (handle) {
      var value = clean(handle);
      if (!value) return false;
      var list = read();
      if (list.indexOf(value) !== -1) return false;
      list.push(value);
      write(list);
      announce(list, value, true);
      return true;
    },

    remove: function (handle) {
      var value = clean(handle);
      if (!value) return false;
      var list = read();
      var at = list.indexOf(value);
      if (at === -1) return false;
      list.splice(at, 1);
      write(list);
      announce(list, value, false);
      return true;
    },

    toggle: function (handle) {
      var value = clean(handle);
      if (!value) return false;
      if (this.has(value)) {
        this.remove(value);
        return false;
      }
      this.add(value);
      return true;
    },

    clear: function () {
      write([]);
      announce([], null, false);
    },

    refresh: function () {
      paint(read());
    }
  };

  window.IbcWishlist = IbcWishlist;

  /* ---- wiring ----------------------------------------------------------- */

  /* One delegated listener covers every heart on the page, including hearts
     added later by quick-add, section re-renders or the wishlist grid. */
  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('[data-ibc-wishlist-toggle]');
    if (!button) return;
    var handle = button.getAttribute('data-ibc-wishlist-handle');
    if (!handle) return;
    /* On a product tile the heart sits in `.ibc-pcard__over`, which is a
       LATER SIBLING of the card's link rather than inside it, so there is no
       card navigation to suppress here. `preventDefault` stays because the
       same button is also rendered inside the product page's buy row, where
       it must not submit the form around it. */
    event.preventDefault();
    IbcWishlist.toggle(handle);
  }

  document.addEventListener('click', onClick);

  /* Another tab changed the list. */
  window.addEventListener('storage', onStorage);

  function onStorage(event) {
    if (event.key && event.key !== KEY) return;
    paint(read());
  }

  function ready() {
    paint(read());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  /* Theme editor: a re-rendered section brings back an unpainted badge. */
  document.addEventListener('shopify:section:load', ready);

  /* A filtered / sorted / paginated grid. assets/ibc-filters.js swaps the
     grid's innerHTML and fires this afterwards; the cards inside it are
     server-rendered and therefore all say "not saved" until now. */
  document.addEventListener('ibc:filters:rendered', ready);

  /* ---- the general hydration net ---------------------------------------- */

  /* Everything else that can put a heart on the page without telling anyone.
     Deliberately narrow: it repaints only when an added element actually
     contains a toggle or a count, and `paint()` no-ops when the state is
     already right, so the common case costs one matches/querySelector per
     added element. */
  function carriesWishlistMarkup(node) {
    if (!node || node.nodeType !== 1) return false;
    if (typeof node.matches === 'function') {
      if (node.matches('[data-ibc-wishlist-toggle], [data-ibc-wishlist-count], [data-ibc-wishlist-label]')) {
        return true;
      }
    }
    if (typeof node.querySelector !== 'function') return false;
    return !!node.querySelector(
      '[data-ibc-wishlist-toggle], [data-ibc-wishlist-count], [data-ibc-wishlist-label]'
    );
  }

  function onMutations(records) {
    if (painting) return;
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (carriesWishlistMarkup(added[j])) {
          paint(read());
          return;
        }
      }
    }
  }

  if (typeof window.MutationObserver === 'function') {
    observer = new window.MutationObserver(onMutations);
    /* childList only. `paint()` writes attributes and text, neither of which
       is watched, so the one self-feeding path is the label's text — and
       that is covered by the `painting` flag and `takeRecords()`. */
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---- teardown ---------------------------------------------------------- */

  /* This controller is page-global: layout/theme.liquid loads it for every
     page because the header's saved count needs it whether or not a card is
     on screen, and it owns nothing that belongs to a section. So it does
     NOT unbind on `shopify:section:unload` — doing that would let the theme
     editor kill the header count by removing an unrelated section, which is
     a worse bug than the leak it would be fixing. There is no per-section
     state to leak: three listeners on document/window and one observer, all
     of which live exactly as long as the document does.

     `destroy()` exists for completeness and for the re-entry guard's sake —
     anything that genuinely needs to replace this controller can unbind the
     old one first rather than stacking a second copy on top of it. */
  IbcWishlist.destroy = function () {
    document.removeEventListener('click', onClick);
    document.removeEventListener('shopify:section:load', ready);
    document.removeEventListener('ibc:filters:rendered', ready);
    document.removeEventListener('DOMContentLoaded', ready);
    window.removeEventListener('storage', onStorage);
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (window.IbcWishlist === IbcWishlist) delete window.IbcWishlist;
  };
})();
